import { Spacing } from '@persona-id/design-tokens';
import { SlidersIcon } from '@persona-id/design-tokens/icons';
import { ColumnOrderState, VisibilityState } from '@tanstack/react-table';
import { useMemo } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
  ResponderProvided,
} from 'react-beautiful-dnd';
import styled from 'styled-components';

import { StyledIconDrag } from 'dashboard/lib/dnd/StyledIconDrag';
import { reorder } from 'dashboard/lib/dnd/utils';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Popover from 'design-system/components/Popover';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';

const TriggerIconButton = (
  <Button title='Configure columns' width='icon' variant='basic'>
    <SlidersIcon />
  </Button>
);

const PopoverCard = styled(Card)`
  width: 300px;
`;

const DndSection = styled('div')`
  max-height: 400px;

  overflow: auto;
`;

interface TableColumnConfigurationPopoverProps {
  columnOrder: ColumnOrderState;
  columnVisibilityState: VisibilityState;
  columnIdToLabel: Record<string, string>;
  updateColumnOrder: (columnOrder: ColumnOrderState) => void;
  updateColumnVisibility: (columnId: string) => void;
}

const TableColumnConfigurationPopover = ({
  columnOrder,
  columnVisibilityState,
  columnIdToLabel,
  updateColumnOrder,
  updateColumnVisibility,
}: TableColumnConfigurationPopoverProps) => {
  const handleShownColumnDragEnd = (result: DropResult, _provided: ResponderProvided) => {
    const { destination, source } = result;
    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }

    // NB: since hidden columns cannot be reordered, we only need to worry about reordering shown columns.
    const reorderedShownColumns = reorder(shownColumnIds, source.index, destination.index);
    updateColumnOrder([...reorderedShownColumns, ...hiddenColumnIds]);
  };

  const generateToggle = (columnName: string) => () => {
    updateColumnVisibility(columnName);
  };

  const shownColumnIds = useMemo(() => {
    return columnOrder.filter((c) => columnVisibilityState[c]);
  }, [columnVisibilityState, columnOrder]);

  const hiddenColumnIds = useMemo(() => {
    return columnOrder.filter((c) => !columnVisibilityState[c]);
  }, [columnVisibilityState, columnOrder]);

  const shownDndProps = shownColumnIds.map((columnId, index) => {
    return {
      columnName: columnIdToLabel[columnId],
      index: index,
      isToggled: true,
      isDisabled: shownColumnIds.length === 1,
      shouldDisableDrag: false,
      handleToggle: generateToggle(columnId),
    };
  });

  const hiddenDndProps = hiddenColumnIds.map((columnId, index) => {
    return {
      columnName: columnIdToLabel[columnId],
      index: index,
      isToggled: false,
      shouldDisableDrag: true,
      handleToggle: generateToggle(columnId),
    };
  });

  return (
    <Popover trigger={TriggerIconButton} portal={false}>
      <Popover.Content side='bottom' align='end'>
        <PopoverCard elevated>
          <Card.Body>
            <DndSection>
              <Label size='lg' weight='bold'>
                Configure columns
              </Label>
              {shownColumnIds.length > 0 && (
                <div>
                  <Paragraph>Shown attributes</Paragraph>
                  <DragDropContext onDragEnd={handleShownColumnDragEnd}>
                    <Droppable
                      droppableId='table__configure-shown-columns'
                      renderClone={(provided, _snapshot, rubric) => {
                        return (
                          <div
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            ref={provided.innerRef}
                          >
                            <ColumnDnd {...shownDndProps[rubric.source.index]} />
                          </div>
                        );
                      }}
                    >
                      {(provided) => {
                        return (
                          <div ref={provided.innerRef} {...provided.droppableProps}>
                            {shownDndProps.map((props, idx) => {
                              return (
                                <Draggable
                                  key={props.columnName}
                                  draggableId={props.columnName}
                                  index={idx}
                                  isDragDisabled={props.shouldDisableDrag}
                                >
                                  {(provided) => {
                                    return (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                      >
                                        <ColumnDnd {...props} />
                                      </div>
                                    );
                                  }}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </div>
                        );
                      }}
                    </Droppable>
                  </DragDropContext>
                </div>
              )}
              <Spacer height={2} />
              {hiddenColumnIds.length > 0 && (
                <div>
                  <Paragraph>Hidden attributes</Paragraph>
                  {/* NB: Hidden attributes cannot be reordered, so no DragDropContext is needed. */}
                  {hiddenDndProps.map((_, idx) => {
                    return (
                      <div key={hiddenDndProps[idx].columnName}>
                        <ColumnDnd {...hiddenDndProps[idx]} />
                      </div>
                    );
                  })}
                </div>
              )}
            </DndSection>
          </Card.Body>
        </PopoverCard>
      </Popover.Content>
    </Popover>
  );
};

const DndItemWrapper = styled('div')`
  background: white;
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
  padding: ${Spacing[2]} ${Spacing[2]};

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: left;
`;

const SuffixContainer = styled('div')`
  padding-left: ${Spacing[2]};
  margin-left: auto;
  align-self: center;
`;

const OverflowText = styled('div')`
  max-width: 160px;
  flex-wrap: wrap;
  display: flex;
`;

interface ColumnDndProps {
  columnName: string;
  index: number;
  isDisabled?: boolean;
  isToggled: boolean;
  shouldDisableDrag: boolean;
  handleToggle: () => void;
}

const ColumnDnd = ({
  columnName,
  index,
  isDisabled = false,
  isToggled,
  shouldDisableDrag,
  handleToggle,
}: ColumnDndProps) => {
  return (
    <DndItemWrapper key={`${columnName}-${index}`}>
      <StyledIconDrag $active $shouldHide={shouldDisableDrag} />
      <OverflowText>
        <Paragraph color='primary' size='sm'>
          {columnName}
        </Paragraph>
      </OverflowText>
      <SuffixContainer>
        <ToggleSlider value={isToggled} disabled={isDisabled} onChange={handleToggle} />
      </SuffixContainer>
    </DndItemWrapper>
  );
};

export default TableColumnConfigurationPopover;
